import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/cash-application`;
const PAGE_SIZE = 50;

interface Payment { id: number; payment_reference: string; customer_id: string; amount: number; applied_amount: number; unapplied_amount: number; match_status: string; payment_date: string; payment_method: string; currency: string; overpayment_amount: number; source_system?: string; status?: string; }
interface Summary { total: number; matched: number; partial: number; unmatched: number; overpaid: number; total_unapplied: number; }
interface Match { id: number; invoice_id: number; match_type: string; confidence_pct: number; applied_amount: number; match_notes: string; }

export default function CashApplication() {
  const { entity } = useGlobalEntity();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState<{ paymentId: number; matches: Match[] } | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ payment_reference: '', customer_id: '', amount: '', payment_method: 'WIRE', currency: 'USD', bank_reference: '' });

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('match_status', statusFilter);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/payments?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setPayments(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, entity]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      const res = await authFetch(`${API}/payments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Payment created', true); setShowCreate(false); setForm({ payment_reference: '', customer_id: '', amount: '', payment_method: 'WIRE', currency: 'USD', bank_reference: '' }); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleAutoMatch = async (id: number) => {
    try {
      const res = await authFetch(`${API}/payments/${id}/auto-match`, { method: 'POST' });
      if (res.ok) { const d = await res.json(); showToast(`${d.matches_found} match(es) found`, true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'No match', false); }
    } catch { showToast('Match failed', false); }
  };

  const viewMatches = async (paymentId: number) => {
    try {
      const res = await authFetch(`${API}/payments/${paymentId}/matches`);
      if (res.ok) { const d = await res.json(); setSelectedMatches({ paymentId, matches: d.matches }); }
    } catch { showToast('Failed to load matches', false); }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };
  const matchTypeColor = (t: string) => t === 'EXACT' ? 'var(--positive)' : t === 'FUZZY' ? 'var(--warning)' : t === 'MULTI_INVOICE' ? 'var(--accent)' : 'var(--text-secondary)';

  const sourcePill = (src?: string) => {
    if (src === 'BANK_INGEST') return <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af' }}>BANK</span>;
    if (src === 'AW_MANUAL') return <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#f3f4f6', color: '#374151' }}>MANUAL</span>;
    return src ? <span style={{ fontSize: 9, color: 'var(--text-hint)' }}>{src}</span> : null;
  };

  const columns: Column[] = [
    { key: 'payment_reference', label: 'Reference', render: (v: string, row: Payment) => (
      <div>
        <div style={{ fontWeight: 600, color: 'var(--accent)' }}>{v || `PMT-${row.id}`}</div>
        <div style={{ marginTop: 2 }}>{sourcePill(row.source_system)}</div>
      </div>
    )},
    { key: 'customer_id', label: 'Customer', render: (v: string) => v ? <span>{v}</span> : <span style={{ color: 'var(--text-hint)', fontSize: 11, fontStyle: 'italic' }}>unresolved</span> },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'applied_amount', label: 'Applied', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'unapplied_amount', label: 'Unapplied', align: 'right', render: (v: number) => <span style={{ color: v > 0 ? 'var(--negative)' : 'var(--text-secondary)' }}>{formatAmount(v)}</span> },
    { key: 'payment_date', label: 'Date', render: (v: string) => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v || '-'}</span> },
    { key: 'match_status', label: 'Status', render: (v: string, row: Payment) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
        <StatusBadge status={v} />
        {row.status === 'POSTED_ON_ACCOUNT' && (
          <span title="SAP F-30 post-on-account: cash already in GL; awaits invoice match (F-32 clearance)"
                style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#fef3c7', color: '#92400e' }}>
            ON ACCOUNT
          </span>
        )}
      </div>
    )},
    { key: 'actions', label: 'Actions', render: (_: any, p: Payment) => (
      <div style={{ display: 'flex', gap: 4 }}>
        {['UNMATCHED', 'PARTIAL'].includes(p.match_status) && <ActionButton label="Auto-Match" onClick={(e) => { e.stopPropagation(); handleAutoMatch(p.id); }} variant="primary" size="sm" />}
        {p.match_status !== 'UNMATCHED' && <ActionButton label="Matches" onClick={(e) => { e.stopPropagation(); viewMatches(p.id); }} variant="secondary" size="sm" />}
      </div>
    )},
  ];

  const matchColumns: Column[] = [
    { key: 'invoice_id', label: 'Invoice', render: (v: number) => <span style={{ fontWeight: 600 }}>INV #{v}</span> },
    { key: 'match_type', label: 'Type', render: (v: string) => <span style={{ color: matchTypeColor(v), fontWeight: 600, fontSize: 11 }}>{v}</span> },
    { key: 'confidence_pct', label: 'Confidence', render: (v: number) => `${v}%` },
    { key: 'applied_amount', label: 'Applied', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'match_notes', label: 'Notes', render: (v: string) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v || '-'}</span> },
  ];

  return (
    <PageLayout
      title="Cash Application"
      subtitle="Match customer payments to open AR invoices -- 4-pass auto-matching"
      action={{ label: '+ Record Payment', onClick: () => setShowCreate(true) }}
    >
      {summary && (
        <KpiGrid items={[
          { label: 'Total Payments', value: summary.total, color: 'var(--text-primary)' },
          { label: 'Matched', value: summary.matched, color: 'var(--positive)' },
          { label: 'Partial', value: summary.partial, color: 'var(--warning)' },
          { label: 'Unmatched', value: summary.unmatched, color: 'var(--negative)' },
          { label: 'Unapplied Total', value: formatAmountCompact(summary.total_unapplied), color: 'var(--negative)' },
        ]} />
      )}

      <FilterBar filters={[
        { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, width: 200, options: ['UNMATCHED', 'PARTIAL', 'MATCHED', 'OVERPAID'].map(s => ({ value: s, label: s })) },
      ]} />

      <DataTable
        columns={columns}
        rows={payments}
        loading={loading}
        emptyMessage="No payments found."
        rowKey="id"
      />

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', alignSelf: 'center' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Record Payment" footer={<><button onClick={() => setShowCreate(false)} style={{ padding: '6px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button><button onClick={handleCreate} style={{ padding: '6px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Record</button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Payment Reference" value={form.payment_reference} onChange={e => setForm({ ...form, payment_reference: e.target.value })} style={inputStyle} />
            <input placeholder="Customer ID *" value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Amount *" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} />
            <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="WIRE">Wire Transfer</option>
              <option value="CHECK">Check</option>
              <option value="ACH">ACH</option>
              <option value="CARD">Card</option>
            </select>
          </div>
          <input placeholder="Bank Reference" value={form.bank_reference} onChange={e => setForm({ ...form, bank_reference: e.target.value })} style={inputStyle} />
        </div>
      </Modal>

      <Modal open={!!selectedMatches} onClose={() => setSelectedMatches(null)} title={`Match Details -- Payment #${selectedMatches?.paymentId}`} footer={<button onClick={() => setSelectedMatches(null)} style={{ padding: '6px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Close</button>}>
        {selectedMatches && selectedMatches.matches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>No matches recorded yet.</div>
        ) : selectedMatches && (
          <DataTable
            columns={matchColumns}
            rows={selectedMatches.matches}
            rowKey="id"
          />
        )}
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
